import * as React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchWaiverRequests } from '../../services/waivers';
import useUser from '../global/useUser';
import useFeatureGuard from '../subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import { AppContext } from '../../context/AppContext/context';
import useGetWaivers from './useGetWaivers';
import useSort from '../global/useSort';
import { SORTABLE_KEYS, WAIVERS_PAGE_SIZE } from '../../constants/waivers';
import { GetWaiverRequestsResponse } from '../../types/waivers';

interface UseGetWaiverRequestsProps {
  waiverId: string | undefined | null;
}

function useGetWaiverRequests({ waiverId }: UseGetWaiverRequestsProps) {
  const { data: waiversData, isLoading: isLoadingWaivers } = useGetWaivers();
  const { user } = useUser();
  const canManageWaivers = useFeatureGuard(Feature.WAIVERS);
  const sort = useSort(SORTABLE_KEYS);

  const hasWaiver = !!waiversData?.published?.enabled;

  const enabled =
    !!user && !!user?.business?.id && canManageWaivers && hasWaiver;

  const { state } = React.useContext(AppContext);
  const { waiversTablePage } = state;

  const queryKey = React.useMemo(
    () => [
      'waiver-requests',
      {
        businessId: user?.business?.id,
        page: waiversTablePage,
        sortOrderBy: sort.orderBy,
        sortOrder: sort.order,
        waiverId,
      },
    ],
    [sort.order, sort.orderBy, user?.business?.id, waiversTablePage, waiverId],
  );

  const queryFn = React.useCallback(async () => {
    if (!waiverId) {
      return { waiverRequests: [], totalCount: 0 } as GetWaiverRequestsResponse;
    }
    const data = await fetchWaiverRequests({
      businessId: user?.business?.id!,
      page: waiversTablePage,
      sort,
      waiverId,
    });
    return data;
  }, [sort, user?.business?.id, waiverId, waiversTablePage]);

  const { data, isPending, isError, isFetching, isRefetching, refetch } =
    useQuery({
      queryKey,
      queryFn,
      placeholderData: keepPreviousData,
      enabled,
      staleTime: 300000,
    });

  const totalPages = Math.ceil((data?.totalCount || 0) / WAIVERS_PAGE_SIZE);
  const hasNext = waiversTablePage + 1 < totalPages;
  const hasPrevious = waiversTablePage > 0;

  return {
    sort,
    waiverRequests: data?.waiverRequests,
    totalCount: data?.totalCount,
    isPending: isPending || isLoadingWaivers,
    isError,
    isFetching,
    isRefetching,
    refetch,
    waiversTablePage,
    enabled,
    hasNext,
    hasPrevious,
  };
}

export default useGetWaiverRequests;
